import { FC, useState, useEffect } from 'react'

import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import {
  useGetAllOffersQuery,
  useDeleteOfferMutation,
  useCloneOfferMutation,
} from 'src/Redux/api/offersApi.ts'
import { RoutesEnum } from 'src/Router/types.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import {
  SearchBlock,
  SearchOffersFormData,
} from 'src/Pages/Offers/SearchBlock.tsx'
import { device } from 'src/Helpers/constants.ts'
import { TableHeader } from 'src/Pages/Offers/TableHeader.tsx'
import { Modal } from 'src/Components/Modal'
import { Button } from 'src/Components/Button'

import { OffersTable } from './OffersTable.tsx'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
    width: 100%;
  }
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
`

const ModalText = styled.p`
  font-size: 16px;
  text-align: center;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`

export const OffersPage: FC = () => {
  const [pageNumber, setPageNumber] = useState(1)
  const [offerToDelete, setOfferToDelete] = useState<number | null>(null)
  const [
    isDeleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const navigate = useNavigate()

  const { control, watch } = useForm<SearchOffersFormData>()

  const { searchTerm, selectedClientId, selectedManagerId } = watch()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000)

  useEffect(() => {
    setPageNumber(1)
  }, [debouncedSearchTerm, selectedClientId, selectedManagerId])

  const [deleteOffer] = useDeleteOfferMutation()
  const [cloneOffer] = useCloneOfferMutation()

  const { data: offersResponse, isLoading } = useGetAllOffersQuery({
    pageNumber,
    searchString: debouncedSearchTerm,
    clientId: Number(selectedClientId),
    managerId: Number(selectedManagerId),
    limit: 20,
  })

  const onAddNewOffer = (): void => {
    navigate(`${RoutesEnum.OFFERS}${RoutesEnum.NEW}`)
  }

  const onLoadMore = (): void => {
    setPageNumber(pageNumber + 1)
  }

  const onCreateContract = (offerNumber: number): void => {
    navigate(`${RoutesEnum.CONTRACTS}${RoutesEnum.NEW}/${offerNumber}`)
  }

  const onViewOffer = (id: number): void => {
    navigate(`${RoutesEnum.OFFERS}/${id}`)
  }

  const onDeleteOffer = (id: number): void => {
    setOfferToDelete(id)
    openDeleteModal()
  }

  const onCloneOffer = (id: number): void => {
    cloneOffer(id)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Коммерческое предложение успешно клонировано',
          message: '',
          position: 'top-right',
        })
        setPageNumber(1)
      })
      .catch(() => {
        notifications.show({
          title: 'Ошибка при клонировании коммерческого предложения',
          message: '',
          position: 'top-right',
          color: 'red',
        })
      })
  }

  const confirmDelete = (): void => {
    if (offerToDelete === null) return

    deleteOffer(offerToDelete)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Коммерческое предложение успешно удалено',
          message: '',
          position: 'top-right',
        })
        // Сбрасываем пагинацию на первую страницу для обновления списка
        setPageNumber(1)
        closeDeleteModal()
        setOfferToDelete(null)
      })
      .catch(() => {
        notifications.show({
          title: 'Ошибка при удалении коммерческого предложения',
          message: '',
          position: 'top-right',
          color: 'red',
        })
        closeDeleteModal()
        setOfferToDelete(null)
      })
  }

  const cancelDelete = (): void => {
    closeDeleteModal()
    setOfferToDelete(null)
  }

  return (
    <Container>
      <TitleWrapper>
        <PageTitle>Коммерческие предложения</PageTitle>

        <SearchBlock onAddNewOffer={onAddNewOffer} control={control} />
      </TitleWrapper>
      <TableHeader />

      <OffersTable
        isLoading={isLoading}
        offers={offersResponse?.data}
        onLoadMore={onLoadMore}
        hasNextPage={offersResponse?.meta.hasNextPage}
        onViewOffer={onViewOffer}
        onCreateContract={onCreateContract}
        onCloneOffer={onCloneOffer}
        onDeleteOffer={onDeleteOffer}
      />

      <Modal
        opened={isDeleteModalOpened}
        onClose={cancelDelete}
        title='Подтверждение удаления'
        centered
      >
        <ModalContent>
          <ModalText>
            Вы уверены, что хотите удалить это коммерческое предложение?
          </ModalText>
          <ButtonGroup>
            <Button onClick={cancelDelete}>Отмена</Button>
            <Button onClick={confirmDelete} data-testid='confirm-delete-offer'>
              Удалить
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  )
}
